<template>
  <div>
    <main-large-dialog
      v-bind="$props"
      :is-visible.sync="computedIsVisible"
      :title="$i18n.t('dialog.title.manageActions')"
      no-paddings-y
      no-paddings-x
      dialog-width="58%"
      max-height="90vh"
      bodyHeight="70vh"
      header-shadow
      @modalClosed="onClose"
      disagree-text="dialog.disagreeClose"
      :has-agree="false"
      :is-valid="isValid"
    >
      <template #body>
        <v-row no-gutters class="fill-height">
          <v-col cols="6" class="fill-height leftCol">
            <v-row no-gutters class="justify-end">
              <v-col cols="auto">
                <base-button
                  translation-path="button.addAction"
                  style="margin-right: 0"
                  @click="openNameDialog"
                ></base-button>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <ZDMActionList
                  :actions="actions"
                  @actionSelected="selectAction"
                  @actionDelete="openDeleteDialog"
                  @actionEdit="openNameDialog"
                  :active-item-id="activeItemId"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6" class="fill-height rightCol">
            <ZDMActionPreview :active-item="activeItem"></ZDMActionPreview>
          </v-col>
        </v-row>
      </template>
    </main-large-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.deleteAction')"
      :body="$i18n.t('dialog.body.deleteActionConfirmation')"
      agree-text="dialog.agreeDelete"
      @agree="handleDelete"
    ></main-dialog>
    <main-input-dialog
      ref="inputDialog"
      v-if="isEditDialogLoaded"
      :is-visible.sync="isEditDialogVisible"
      :inputs="nameDialogInputs"
      :agree-text="nameDialogAgreeText"
      dialog-width="40%"
      :title-translation="nameDialogTitle"
      @agree="computedAgreeAction"
    />
  </div>
</template>

<script>
import { call } from 'vuex-pathify';

import BaseButton from '@/components/BaseButton';
import ZDMActionList from '@/components/ZDMActionList';
import ZDMActionPreview from '@/components/ZDMActionPreview';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'ZDMActionsDialog',
  components: {
    ZDMActionPreview,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
    ZDMActionList,
    BaseButton,
    MainLargeDialog,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isEditDialogLoaded: false,
    isEditDialogVisible: false,
    activeItemId: 0,
    deleteId: 0,
    actionToEdit: undefined,
  }),
  computed: {
    // actions: get('zdm_data/zdmActions'),
    nameDialogTitle() {
      return this.actionToEdit === undefined
        ? this.$i18n.t('dialog.title.addAction')
        : this.$i18n.t('dialog.title.editAction');
    },
    nameDialogAgreeText() {
      return this.actionToEdit === undefined ? 'dialog.agreeAdd' : 'dialog.agreeSave';
    },
    nameDialogInputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.name',
          clearable: true,
          dataType: {
            name: 'text',
          },
          rules: [v => !!v || v === undefined || 'rules.required'],
          value: this.actionToEdit ? this.actionToEdit.name : '',
        },
        {
          name: 'district',
          translationPath: 'dialog.body.district',
          clearable: true,
          dataType: {
            name: 'text',
          },
          rules: [v => !!v || v === undefined || 'rules.required'],
          value: this.actionToEdit ? this.actionToEdit.district : '',
        },
        {
          name: 'street',
          translationPath: 'dialog.body.street',
          clearable: true,
          dataType: {
            name: 'text',
          },
          rules: [v => !!v || v === undefined || 'rules.required'],
          value: this.actionToEdit ? this.actionToEdit.street : '',
        },
      ];
    },
    computedAgreeAction() {
      return this.actionToEdit === undefined ? this.handleActionAdd : this.handleActionEdit;
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    isValid() {
      return true;
    },
    activeItem() {
      return this.actions.find(item => item.id === this.activeItemId);
    },
  },
  methods: {
    editAction: call('zdm_data/editAction'),
    deleteAction: call('zdm_data/deleteAction'),
    addAction: call('zdm_data/addAction'),
    openNameDialog(id) {
      this.actionToEdit = this.actions.find(value => value.id === id);
      this.isEditDialogLoaded = true;
      this.isEditDialogVisible = true;
    },
    async handleActionAdd() {
      const values = this.nameDialogInputs.map(input => {
        return {
          [input.name]: input.value,
        };
      });
      const payload = values.reduce((r, c) => Object.assign(r, c), {});
      try {
        await this.addAction(payload);
      } catch (e) {
        console.log(e);
      } finally {
        this.isEditDialogLoaded = false;
        this.isEditDialogVisible = false;
        this.actionToEdit = undefined;
        this.$refs.inputDialog.$refs.form.reset();
      }
    },
    async handleActionEdit() {
      const values = this.nameDialogInputs.map(input => {
        return {
          [input.name]: input.value,
        };
      });
      const payload = values.reduce((r, c) => Object.assign(r, c), {});
      try {
        await this.editAction({ id: this.actionToEdit.id, body: payload });
      } catch (e) {
        console.log(e);
      } finally {
        this.actionToEdit = undefined;
        this.$refs.inputDialog.$refs.form.reset();
        this.isEditDialogLoaded = false;
        this.isEditDialogVisible = false;
      }
    },
    selectAction(a) {
      this.activeItemId = a;
    },
    openDeleteDialog(id) {
      this.deleteId = id;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    async handleDelete() {
      try {
        await this.deleteAction(this.deleteId);
      } catch (e) {
        console.log(e);
      } finally {
        this.isDeleteDialogLoaded = false;
        this.isDeleteDialogVisible = false;
      }
    },
    onClose() {
      this.computedIsVisible = false;
      this.$emit('modalClosed');
    },
  },
};
</script>

<style scoped lang="scss">
.leftCol {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  padding: 20px;
}
.rightCol {
  padding: 20px;
}
</style>
